import React, { useEffect, useRef } from 'react';
import { useLanguage } from '../i18n/LanguageContext';

const OTP_LENGTH = 6;

/** Single invisible input + digit boxes — best for Android SMS autofill. */
export default function LoginOtpInput({
  value = '',
  onChange,
  onComplete,
  disabled = false,
  autoFocus = true,
}) {
  const { t } = useLanguage();
  const inputRef = useRef(null);
  const code = String(value || '').replace(/\D/g, '').slice(0, OTP_LENGTH);
  const activeIndex = Math.min(code.length, OTP_LENGTH - 1);
  const completedRef = useRef('');

  const applyCode = (raw) => {
    const next = String(raw || '').replace(/\D/g, '').slice(0, OTP_LENGTH);
    onChange?.(next);
    if (next.length === OTP_LENGTH && next !== completedRef.current) {
      completedRef.current = next;
      onComplete?.(next);
    }
    if (next.length < OTP_LENGTH) completedRef.current = '';
  };

  useEffect(() => {
    if (!autoFocus || disabled) return undefined;
    const timer = setTimeout(() => inputRef.current?.focus(), 200);
    return () => clearTimeout(timer);
  }, [autoFocus, disabled]);

  return (
    <div className="login-otp-field">
      <div
        className="login-otp-row"
        onClick={() => { if (!disabled) inputRef.current?.focus(); }}
        role="presentation"
      >
        {Array.from({ length: OTP_LENGTH }, (_, i) => {
          const digit = code[i] || '';
          const isActive = !disabled && i === activeIndex;
          return (
            <div
              key={i}
              className={`login-otp-box${digit ? ' login-otp-box--filled' : ''}${isActive ? ' login-otp-box--active' : ''}`}
              aria-hidden="true"
            >
              {digit}
            </div>
          );
        })}
        <input
          ref={inputRef}
          className="login-otp-input-overlay"
          type="tel"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="one-time-code"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          enterKeyHint="done"
          maxLength={OTP_LENGTH}
          value={code}
          disabled={disabled}
          aria-label={t('login.otpAriaLabel')}
          onChange={(e) => applyCode(e.target.value)}
          onPaste={(e) => {
            e.preventDefault();
            applyCode(e.clipboardData.getData('text'));
          }}
        />
      </div>
    </div>
  );
}
